import { useEffect, useRef, useState } from "react";
import { Upload, X, Image as ImageIcon, CheckCircle2, Info, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { ConfirmationMessage, ErrorMessage } from "@/components/ui/States";
import { BUSINESS, PHASE3_BRAND_VOICE } from "@/lib/constants";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const ACCEPTED = ["image/jpeg", "image/png", "image/heic", "image/heif"];
const MAX_SIZE = 15 * 1024 * 1024;
const MAX_FILES = 10;

const FALLBACK_UNAVAILABLE_MESSAGE =
    "Photo upload is not active yet. You can still submit your estimate request, and A1 Screening can review photos later.";

// Direct-to-Cloudinary signed upload with progress. Bytes never touch our API.
function uploadToCloudinary(file, sig, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        const form = new FormData();
        form.append("file", file);
        form.append("api_key", sig.api_key);
        form.append("timestamp", sig.timestamp);
        form.append("signature", sig.signature);
        form.append("folder", sig.folder);
        xhr.open("POST", `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`);
        xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
            else reject(new Error(`Upload failed (${xhr.status})`));
        };
        xhr.onerror = () => reject(new Error("Upload failed"));
        xhr.send(form);
    });
}

export default function PhotoUploader() {
    const { settings: siteSettings } = useSiteSettings();
    const uploadActive = !!siteSettings?.photo_upload_active;
    const unavailableMessage = siteSettings?.photo_upload_unavailable_message || FALLBACK_UNAVAILABLE_MESSAGE;

    const [files, setFiles] = useState([]);
    const [phone, setPhone] = useState("");
    const [name, setName] = useState("");
    const [city, setCity] = useState("Naples");
    const [email, setEmail] = useState("");
    const [notes, setNotes] = useState("");
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const fileInput = useRef(null);
    const startMs = useRef(Date.now());

    useEffect(() => { startMs.current = Date.now(); }, []);

    const patchFile = (id, patch) =>
        setFiles((cur) => cur.map((f) => (f.id === id ? { ...f, ...patch } : f)));

    const onSelect = (selected) => {
        if (!selected || !selected.length) return;
        if (!uploadActive) return;
        track(EVENTS.PHOTO_UPLOAD_START, { count: selected.length });
        const next = [...files];
        for (const file of Array.from(selected)) {
            if (next.length >= MAX_FILES) break;
            if (!ACCEPTED.includes(file.type)) {
                setError(`Unsupported file: ${file.name}. Please send JPG, PNG, or HEIC.`);
                continue;
            }
            if (file.size > MAX_SIZE) {
                setError(`File too large: ${file.name}. Max 15 MB.`);
                continue;
            }
            next.push({
                id: `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 6)}`,
                file,
                previewUrl: URL.createObjectURL(file),
                status: "ready",
                progress: 0,
            });
        }
        setFiles(next.slice(0, MAX_FILES));
    };

    const remove = (id) => setFiles((cur) => cur.filter((f) => f.id !== id));

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!phone.trim() || !consent) {
            setError("Please give us a phone number and consent so we can follow up.");
            return;
        }
        setSubmitting(true);

        // Phase 1 — upload photos directly to Cloudinary (when active).
        const uploadedMeta = [];
        if (uploadActive && files.length) {
            let sig = null;
            try {
                sig = (await api.post("/public/photos/signature")).data;
            } catch (sigErr) {
                setError(
                    sigErr?.response?.data?.detail
                    || "Photo upload isn't responding right now. You can submit without photos and text them to us after we connect."
                );
            }
            if (sig) {
                for (const f of files) {
                    if (f.status === "done" && f.uploaded) {
                        uploadedMeta.push(f.uploaded);
                        continue;
                    }
                    patchFile(f.id, { status: "uploading", progress: 0 });
                    try {
                        const resp = await uploadToCloudinary(f.file, sig, (p) => patchFile(f.id, { progress: p }));
                        const meta = { public_id: resp.public_id, url: resp.secure_url, filename: f.file.name };
                        uploadedMeta.push(meta);
                        patchFile(f.id, { status: "done", progress: 100, uploaded: meta });
                    } catch {
                        patchFile(f.id, { status: "failed" });
                    }
                }
            }
        }

        // Phase 2 — submit the lead with whatever uploaded successfully.
        try {
            const res = await api.post("/leads/photo-upload", {
                name: name.trim() || null,
                phone: phone.trim(),
                email: email.trim() || null,
                city: city.trim() || "Naples",
                notes: notes.trim() || null,
                photo_ids: uploadedMeta.map((m) => m.public_id),
                uploaded_photos: uploadedMeta,
                consent_given: true,
                website_url: honeypot,
                start_ms: startMs.current,
            });
            track(EVENTS.PHOTO_UPLOAD_SUBMIT, {
                count: uploadedMeta.length,
                lead_id: res.data?.lead_id,
                storage_active: uploadActive,
            });
            setSuccess(res.data.confirmation_message);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(
                detail
                || `Sorry — that didn't go through. Please call ${BUSINESS.phone} or email ${BUSINESS.email} and we'll take it from there.`
            );
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return <ConfirmationMessage message={success} testId="photo-upload-success" />;
    }

    return (
        <form onSubmit={submit} className="grid gap-4" data-testid="photo-uploader" noValidate>
            <p className="text-[var(--color-text-muted)]">
                {PHASE3_BRAND_VOICE.photo_human_review}
            </p>
            <p className="text-[var(--color-text-muted)] text-sm">
                {PHASE3_BRAND_VOICE.photo_helpful_use}
            </p>

            {uploadActive ? null : (
                <div
                    className="rounded-md p-4 flex items-start gap-3"
                    style={{
                        background: "rgba(255, 214, 95, 0.08)",
                        border: "1px solid var(--color-warning)",
                    }}
                    data-testid="photo-upload-unavailable-notice"
                    role="status"
                >
                    <Info size={20} className="text-[var(--color-warning)] shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm leading-relaxed m-0" style={{ color: "var(--color-text)" }}>
                            <strong>{unavailableMessage}</strong>
                        </p>
                        <p className="text-xs mt-2 m-0" style={{ color: "var(--color-text-muted)" }}>
                            You can still text photos to <a href={`tel:${BUSINESS.phoneTel}`} className="underline">{BUSINESS.phone}</a> or email them to <a href={`mailto:${BUSINESS.email}`} className="underline">{BUSINESS.email}</a> after you submit the form below.
                        </p>
                    </div>
                </div>
            )}

            {uploadActive ? (
                <>
                    <div
                        className="border-2 border-dashed border-[var(--color-line)] rounded p-6 text-center cursor-pointer hover:bg-[var(--color-bg-alt)]"
                        onClick={() => fileInput.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => {
                            e.preventDefault();
                            onSelect(e.dataTransfer.files);
                        }}
                        data-testid="photo-dropzone"
                    >
                        <Upload size={32} className="mx-auto mb-2" style={{ color: "var(--color-accent)" }} />
                        <p className="font-semibold">Tap or drop photos</p>
                        <p className="a1-helper">JPG, PNG, or HEIC. Up to 10 photos. 15 MB each.</p>
                        <input
                            ref={fileInput}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            className="hidden"
                            onChange={(e) => onSelect(e.target.files)}
                            data-testid="photo-input"
                        />
                    </div>

                    {files.length > 0 ? (
                        <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                            {files.map((f) => (
                                <div key={f.id} className="relative a1-card p-2" data-testid={`photo-thumb-${f.id}`}>
                                    {f.previewUrl ? (
                                        <img src={f.previewUrl} alt={f.file.name} className="aspect-square object-cover rounded w-full" />
                                    ) : (
                                        <div className="aspect-square bg-[var(--color-bg-alt)] flex items-center justify-center">
                                            <ImageIcon size={28} />
                                        </div>
                                    )}
                                    {f.status !== "uploading" ? (
                                        <button
                                            type="button"
                                            onClick={() => remove(f.id)}
                                            className="absolute -top-2 -right-2 bg-[var(--color-danger)] text-white rounded-full w-6 h-6 flex items-center justify-center"
                                            aria-label="Remove photo"
                                            data-testid={`photo-remove-${f.id}`}
                                        >
                                            <X size={14} />
                                        </button>
                                    ) : null}
                                    <p className="text-xs mt-1 truncate">{f.file.name}</p>
                                    {f.status === "uploading" ? (
                                        <div className="upload-progress" role="progressbar" aria-valuenow={f.progress} aria-valuemin={0} aria-valuemax={100}>
                                            <span style={{ width: `${f.progress}%` }} />
                                        </div>
                                    ) : f.status === "done" ? (
                                        <p className="text-xs text-[var(--color-success)] flex items-center gap-1">
                                            <CheckCircle2 size={12} /> Uploaded
                                        </p>
                                    ) : f.status === "failed" ? (
                                        <p className="text-xs text-[var(--color-danger)] flex items-center gap-1">
                                            <AlertTriangle size={12} /> Failed — will skip
                                        </p>
                                    ) : (
                                        <p className="text-xs text-[var(--color-text-muted)] flex items-center gap-1">
                                            <CheckCircle2 size={12} /> Ready
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </>
            ) : null}

            <p className="a1-helper italic">
                We only use your photos to prepare your estimate. We don't share or sell them. Photos are never published on the public gallery without your written permission.
            </p>

            <div className="grid gap-4 md:grid-cols-2">
                <div>
                    <label className="a1-label" htmlFor="pu-name">Your Name (optional)</label>
                    <input id="pu-name" className="a1-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" data-testid="pu-name" />
                </div>
                <div>
                    <label className="a1-label" htmlFor="pu-phone">Phone Number <span className="text-[var(--color-danger)]">*</span></label>
                    <input id="pu-phone" type="tel" inputMode="tel" className="a1-input" value={phone} onChange={(e) => setPhone(e.target.value)} required autoComplete="tel" data-testid="pu-phone" />
                </div>
                <div>
                    <label className="a1-label" htmlFor="pu-email">Email (optional)</label>
                    <input id="pu-email" type="email" inputMode="email" className="a1-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="pu-email" />
                </div>
                <div>
                    <label className="a1-label" htmlFor="pu-city">City</label>
                    <input id="pu-city" className="a1-input" value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" data-testid="pu-city" />
                </div>
            </div>

            <div>
                <label className="a1-label" htmlFor="pu-notes">Anything else? (optional)</label>
                <textarea id="pu-notes" className="a1-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pu-notes" />
            </div>

            <label className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" required data-testid="pu-consent" />
                <span>By submitting this form, you agree that A1 Screening may contact you by phone, text, or email about your service request. Message and data rates may apply. Message frequency varies. Reply STOP to opt out of texts. See our <a href="/privacy-policy">Privacy Policy</a> and <a href="/terms">Terms</a>.</span>
            </label>

            <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="a1-honeypot" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} name="website_url" />

            {error ? <ErrorMessage message={error} testId="pu-error" /> : null}

            <button type="submit" className="a1-btn-primary w-full" disabled={submitting} data-testid="pu-submit">
                {submitting ? "Sending…" : (uploadActive ? "Send Photos for Estimate Review" : "Send Estimate Request")}
            </button>

            <p className="a1-helper text-center">
                Or call us — <a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phone}</a> · {BUSINESS.hours}.
            </p>
        </form>
    );
}
